import React from 'react';
import { GripVertical, Clock, Edit2, Trash2, FileAudio, Music, FileText, Plus } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RichTextDisplay } from '@/components/common/RichTextDisplay';
import { PlanElement } from '@/services/api';
import { cn } from '@/lib/utils';
import { generateSongPDF } from '@/lib/generateSongPDF';
import { AttachmentsDialog } from '../../AttachmentsDialog';

/**
 * Sortable row component for hendelse (event) elements
 * 
 * Displays a draggable hendelse with:
 * - Grip handle for drag-and-drop
 * - Start time (calculated)
 * - Duration badge
 * - Event name and description
 * - Edit and delete buttons
 * 
 * Extracted from PlanEditor.tsx for better modularity.
 */

interface SortableHendelseRowProps {
  hendelse: PlanElement;
  activeElement: PlanElement | null;
  calculateStartTime: (id: number) => Date | null;
  formatTime: (date: Date) => string;
  formatDurationShort: (minutter: number) => string;
  onEdit: (element: PlanElement) => void;
  onDelete: (id: number) => void;
  onSelectSong?: (element: PlanElement) => void; // For musikk_placeholder
  readOnly?: boolean;
}

export const SortableHendelseRow: React.FC<SortableHendelseRowProps> = React.memo(({
  hendelse, 
  activeElement,
  calculateStartTime,
  formatTime,
  formatDurationShort,
  onEdit,
  onDelete,
  onSelectSong,
  readOnly = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: hendelse.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const startTime = calculateStartTime(hendelse.id);
  const showDropIndicator = isOver && activeElement?.id !== hendelse.id;
  const [attachmentsDialogOpen, setAttachmentsDialogOpen] = React.useState(false);

  const handleOpenPDF = (element: PlanElement, textOnly: boolean = false) => {
    if (!element.musikk_tekst_og_akkorder || !element.musikk_tittel) {
      console.error('Mangler data for PDF-generering');
      return;
    }

    try {
      // Beregn semitoner basert på valgt transponering
      const semitones = element.transponering_semitoner ?? 0;

      generateSongPDF({
        title: element.musikk_tittel,
        artist: element.musikk_artist,
        tekstOgAkkorder: element.musikk_tekst_og_akkorder,
        semitones,
        originalGrunntone: element.musikk_grunntone,
        originalToneartType: element.musikk_toneart_type,
        columns: 1, // Alltid én kolonne fra plan-visning
        textOnly, // Kun tekst uten akkorder
      });
    } catch (err) {
      console.error('Kunne ikke generere PDF:', err);
    }
  };

  return (
    <div className="relative">
      {showDropIndicator && (
        <div className="absolute -top-1 left-0 right-0 h-0.5 bg-primary z-50 rounded-full shadow-lg">
          <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 bg-primary rounded-full border-2 border-white shadow-lg" />
        </div>
      )}
      
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'bg-card border-b hover:bg-accent/30 transition-all',
          hendelse.type === 'musikk_placeholder' 
            ? 'border-2 border-dashed border-purple-300 bg-purple-50/30' 
            : 'border-border/50',
          isDragging ? 'opacity-0' : '',
          showDropIndicator ? 'ring-2 ring-primary/50' : ''
        )}
      >
        <div className="flex items-start gap-1 sm:gap-2 p-2 sm:p-3">
          {!readOnly && (
            <div 
              {...attributes} 
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex items-center shrink-0"
            >
              <GripVertical className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
          )}

          <div className="text-xs sm:text-sm font-mono text-foreground/80 min-w-[40px] sm:min-w-[50px] shrink-0">
            {startTime ? formatTime(startTime) : '--:--'}
          </div>

          {hendelse.varighet_minutter !== undefined && (
            <Badge 
              variant="outline" 
              className="gap-0.5 sm:gap-1 text-xs bg-primary/10 text-primary border-primary/30 font-mono shrink-0"
            >
              <Clock className="h-3 w-3" />
              <span className="hidden xs:inline">
                {formatDurationShort(hendelse.varighet_minutter)}
              </span>
            </Badge>
          )}

          <div className="flex-1 min-w-0">
            <p className="font-medium text-xs sm:text-sm text-foreground truncate">{hendelse.navn}</p>
            {hendelse.beskrivelse && (
              <div className="mt-0.5 hidden sm:block">
                <RichTextDisplay 
                  content={hendelse.beskrivelse}
                />
              </div>
            )}
          </div>

          {hendelse.type === 'sang' && hendelse.musikk_filer && hendelse.musikk_filer.length > 0 && (
            <Badge 
              variant="outline" 
              className="gap-0.5 sm:gap-1 text-xs bg-muted border-muted-foreground/30 text-muted-foreground shrink-0 cursor-pointer hover:bg-muted/70 transition-colors"
              title={`Klikk for å se ${hendelse.musikk_filer.length} ${hendelse.musikk_filer.length === 1 ? 'vedlegg' : 'vedlegg'}`}
              onClick={(e) => {
                e.stopPropagation();
                setAttachmentsDialogOpen(true);
              }}
            >
              <FileAudio className="h-3 w-3" />
              <span className="hidden xs:inline">{hendelse.musikk_filer.length}</span>
            </Badge>
          )}

          {/* Toneart indikator */}
          {hendelse.type === 'sang' && hendelse.musikk_tekst_og_akkorder && (
            <>
              <Badge 
                variant="outline" 
                className="gap-0.5 sm:gap-1 text-xs bg-blue-100 text-blue-700 border-blue-200 shrink-0 cursor-pointer hover:bg-blue-200 transition-colors"
                title={`Klikk for å åpne PDF med toneart: ${hendelse.transponering_navn || `${hendelse.musikk_grunntone || 'C'} ${hendelse.musikk_toneart_type || 'dur'}`}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPDF(hendelse, false);
                }}
              >
                <Music className="h-3 w-3" />
              </Badge>
              
              {/* Tekst-only PDF indikator */}
              <Badge 
                variant="outline" 
                className="gap-0.5 sm:gap-1 text-xs bg-green-100 text-green-700 border-green-200 shrink-0 cursor-pointer hover:bg-green-200 transition-colors"
                title="Klikk for å åpne PDF med kun sangtekst (uten akkorder)"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPDF(hendelse, true);
                }}
              >
                <FileText className="h-3 w-3" />
              </Badge>
            </>
          )}

          {!readOnly && (
            <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
              {hendelse.type === 'musikk_placeholder' ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSelectSong && onSelectSong(hendelse)}
                  className="gap-1 h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <Plus className="h-3 w-3" />
                  <span className="hidden xs:inline">Velg sang</span>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(hendelse)}
                  className="h-6 w-6 sm:h-7 sm:w-7 text-primary hover:bg-primary/10"
                >
                  <Edit2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                </Button>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(hendelse.id)}
                className="h-6 w-6 sm:h-7 sm:w-7 text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Attachments Dialog */}
      {hendelse.type === 'sang' && hendelse.musikk_id && hendelse.musikk_filer && (
        <AttachmentsDialog
          open={attachmentsDialogOpen}
          onOpenChange={setAttachmentsDialogOpen}
          musikkId={hendelse.musikk_id}
          filer={hendelse.musikk_filer}
          sangTittel={hendelse.musikk_tittel}
        />
      )}
    </div>
  );
});

SortableHendelseRow.displayName = 'SortableHendelseRow';

